// Core packages
import { formatDate } from "@angular/common";
import { Component, Inject } from "@angular/core";

// Third party packages
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material";
import { TranslateService } from "@ngx-translate/core";
import * as FileSaver from "file-saver";
import { ToastrService } from "ngx-toastr";

// Custom packages
import { HelperService } from "src/app/shared/services/helper.service";
import {
  PROPERTY_PRIVACY_TEMPLATE,
  SUPPLIER_PRIVACY_TEMPLATE,
  LANDLORD_PRIVACY_TEMPLATE, PrivacyDocsService,
  COMMON_AREAS_PRIVACY_TEMPLATE,
  EMPLOYEE_PRIVACY_TEMPLATE,
  DATA_PROCESSOR_APPOINTMENT_TEMPLATE,
  PRIVACY_DOC_MIME_EXTENSIONS,
  DEFAULT_PRIVACY_DOC_EXTENSION,
} from "src/app/shared/services/privacy-docs.service";

type PrivacyDocAction = "generate" | "download";

interface IPrivacyDocRow {
  labelKey: string;
  action: PrivacyDocAction;
  /** Back-end template key: only set for templates already supported by PrivacyDocumentService */
  templateType?: string;
  exportFileNameKey?: string;
  enabled: boolean;
  loading: boolean;
}

interface IPrivacyDocsListDialogData {
  propertyId: string;
  companyId: string;
}

/**
 * Script start
 */
@Component({
  selector: "app-privacy-docs-list-dialog",
  templateUrl: "./privacy-docs-list-dialog.component.html",
  styleUrls: ["./privacy-docs-list-dialog.component.scss"]
})
export class PrivacyDocsListDialogComponent {
  rows: IPrivacyDocRow[] = [
    {
      labelKey: "properties.home.privacyDocsDialog.docs.condoOwner",
      action: "generate",
      templateType: PROPERTY_PRIVACY_TEMPLATE,
      exportFileNameKey: "properties.home.privacyDocsDialog.exportFileName.property",
      enabled: true,
      loading: false,
    },
    {
      labelKey: "properties.home.privacyDocsDialog.docs.supplier",
      action: "generate",
      templateType: SUPPLIER_PRIVACY_TEMPLATE,
      exportFileNameKey: "properties.home.privacyDocsDialog.exportFileName.supplier",
      enabled: true,
      loading: false,
    },
    {
      labelKey: "properties.home.privacyDocsDialog.docs.tenantCondoUnit",
      action: "generate",
      templateType: LANDLORD_PRIVACY_TEMPLATE,
      exportFileNameKey: "properties.home.privacyDocsDialog.exportFileName.realEstate",
      enabled: true,
      loading: false,
    },
    {
      labelKey: "properties.home.privacyDocsDialog.docs.tenantCommonAreas",
      action: "generate",
      templateType: COMMON_AREAS_PRIVACY_TEMPLATE,
      exportFileNameKey: "properties.home.privacyDocsDialog.exportFileName.commonAreas",
      enabled: true,
      loading: false,
    },
    {
      labelKey: "properties.home.privacyDocsDialog.docs.employee",
      action: "download",
      templateType: EMPLOYEE_PRIVACY_TEMPLATE,
      exportFileNameKey: "properties.home.privacyDocsDialog.exportFileName.employee",
      enabled: true,
      loading: false,
    },
    {
      labelKey: "properties.home.privacyDocsDialog.docs.dataProcessorAppointment",
      action: "download",
      templateType: DATA_PROCESSOR_APPOINTMENT_TEMPLATE,
      exportFileNameKey: "properties.home.privacyDocsDialog.exportFileName.dataProcessor",
      enabled: true,
      loading: false,
    },
  ];

  /**
   * Class constructor
   */
  constructor(
    public dialogRef: MatDialogRef<PrivacyDocsListDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: IPrivacyDocsListDialogData,
    private privacyDocsService: PrivacyDocsService,
    private translateService: TranslateService,
    private toastrService: ToastrService,
    private helperService: HelperService,
  ) { }

  /**
   * Handle a row's action button click: only rows flagged as "enabled" have
   * a working back-end template (@see PrivacyDocumentService); the others
   * are shown disabled until their template/endpoint is available.
   *
   * @since 1.0.0
   */
  onRowAction(row: IPrivacyDocRow): void {
    if (!row.enabled || row.loading || !row.templateType) {
      return;
    }
    if (!this.data || !this.data.companyId || !this.data.propertyId) {
      this.toastrService.error(
        this.translateService.instant("properties.home.privacyDocsDialog.missingCompany")
      );
      return;
    }

    row.loading = true;
    this.privacyDocsService
      .generateDocuments(this.data.companyId, [this.data.propertyId], row.templateType)
      .subscribe(
        (fileBlob: Blob) => {
          row.loading = false;
          const date = formatDate(new Date(), "yyyyMMddhhmmss", "en-EN");
          const fileNamePrefix = this.translateService.instant(
            row.exportFileNameKey || "properties.home.privacyDocsDialog.exportFileName"
          );
          const extension = PRIVACY_DOC_MIME_EXTENSIONS[fileBlob.type] || DEFAULT_PRIVACY_DOC_EXTENSION;
          FileSaver.saveAs(fileBlob, `${fileNamePrefix}-${date}.${extension}`);
          this.toastrService.success(
            this.translateService.instant("properties.home.privacyDocsDialog.success")
          );
        },
        (err) => {
          row.loading = false;
          this.helperService.handleError(err);
        }
      );
  }

  /**
   * Handle "Chiudi": close the dialog
   *
   * @since 1.0.0
   */
  onClose(): void {
    this.dialogRef.close();
  }
}
